AFRAME.registerComponent("scriptable-model", {
  schema: {
    src: { type: "string", default: "" },
    castShadow: { type: "boolean", default: false },
    receiveShadow: { type: "boolean", default: false },
    billboard: { type: "boolean", default: false },
    collidable: { type: "boolean", default: false },
    walkable: { type: "boolean", default: false },
    enableOnProximity: { type: "boolean", default: false },
    proximityRadius: { type: "number", default: 3 },
    enableMaterialColor: { type: "boolean", default: false },
    materialColor: { type: "color", default: "#ffffff" },
    emissiveColor: { type: "color", default: "#000000" },
    activeClipItems: {
      default: [],
      parse: v => {
        if (Array.isArray(v)) return v;
        if (typeof v === "string" && v.trim().length) {
          try {
            return JSON.parse(v);
          } catch (_) {
            return [];
          }
        }
        return [];
      },
      stringify: v => JSON.stringify(v || [])
    },
    networkable: { type: "boolean", default: false }
  },

  init() {
    this._ready = false;
    this._readyResolvers = [];
    this._pending = {};
    this._syncing = false;
    this._modelRoot = null;
    this._mixer = null;
    this._animations = [];
    this._currentActions = [];
    this._originalColors = { material: {}, emissive: {} };
    this._lastApplied = {};
    this._lastSrc = undefined;

    this.el.object3D.scriptableType = "model";

    this._onModelLoaded = this._onModelLoaded.bind(this);
    this._onModelError = this._onModelError.bind(this);
    this._onComponentChanged = this._onComponentChanged.bind(this);

    this.el.addEventListener("model-loaded", this._onModelLoaded);
    this.el.addEventListener("model-error", this._onModelError);
    this.el.addEventListener("componentchanged", this._onComponentChanged);

    // Preserve any pre-existing transform attributes (position/rotation/scale) on init
    try {
      const pos = this.el.getAttribute("position");
      const rot = this.el.getAttribute("rotation");
      const sca = this.el.getAttribute("scale");
      if (pos) {
        this.el.object3D.position.set(pos.x, pos.y, pos.z);
      }
      if (rot) {
        // Align A-Frame expected rotation order YXZ
        this.el.object3D.rotation.order = "YXZ";
        this.el.object3D.rotation.set(
          THREE.MathUtils.degToRad(rot.x || 0),
          THREE.MathUtils.degToRad(rot.y || 0),
          THREE.MathUtils.degToRad(rot.z || 0)
        );
      }
      if (sca) {
        this.el.object3D.scale.set(sca.x || 1, sca.y || 1, sca.z || 1);
      }
      this.el.object3D.matrixNeedsUpdate = true;
    } catch (_) {
      /* ignore transform sync on init */
    }

    // Sync existing shadow component to our data before queuing props
    this._syncExistingShadowState();

    // Kick off initial load if src provided
    if (this.data.src) {
      this._ensureLoading(this.data.src);
    }

    // Apply non-src props (queued until ready)
    this._queueAllProps();
  },

  remove() {
    this.el.removeEventListener("model-loaded", this._onModelLoaded);
    this.el.removeEventListener("model-error", this._onModelError);
    this.el.removeEventListener("componentchanged", this._onComponentChanged);
    this._readyResolvers.length = 0;
    this._currentActions.forEach(a => {
      try {
        a.stop();
      } catch (_) {
        /* ignore stop errors */
      }
    });
    this._currentActions.length = 0;
    this._mixer = null;
    this._modelRoot = null;
  },

  update(oldData) {
    const d = this.data;

    if (oldData && d.src !== oldData.src) {
      this._resetRuntimeState();
      if (d.src) this._ensureLoading(d.src);
    }

    if (!oldData) return; // init handled queueing already

    // Queue/apply changes
    const keys = Object.keys(this.schema).filter(k => k !== "src");
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (d[k] !== oldData[k]) {
        this._applyOrQueue(k, d[k]);
      }
    }
  },

  whenReady() {
    if (this._ready) return Promise.resolve();
    return new Promise(resolve => this._readyResolvers.push(resolve));
  },

  /* ---------------------------- internal logic ---------------------------- */
  _syncExistingShadowState() {
    // Read existing shadow component if present and sync to our data
    const existingShadow = this.el.getAttribute("shadow");
    if (existingShadow) {
      this._syncing = true;
      const currentData = this.el.getAttribute("scriptable-model") || {};

      // Only sync if shadow values are explicitly set
      if (existingShadow.cast !== undefined) {
        currentData.castShadow = !!existingShadow.cast;
      }
      if (existingShadow.receive !== undefined) {
        currentData.receiveShadow = !!existingShadow.receive;
      }

      this.el.setAttribute("scriptable-model", currentData);
      this._syncing = false;
    }
  },

  _queueAllProps() {
    const d = this.data;
    const keys = Object.keys(this.schema).filter(k => k !== "src");
    for (let i = 0; i < keys.length; i++) {
      this._applyOrQueue(keys[i], d[keys[i]]);
    }
  },

  _ensureLoading(src) {
    if (this._lastSrc === src) return;
    this._lastSrc = src;

    // Drive the standard loader path. It will create animation-mixer because this component exists.
    const current = this.el.getAttribute("media-loader") || {};
    const next = Object.assign({}, current, {
      src,
      fitToBox: false,
      resolve: true,
      fileIsOwned: true,
      animate: false,
      moveTheParentNotTheMesh: true,
      useFancyLoader: false,
      targetComponent: "media-loader"
    });
    this.el.setAttribute("media-loader", next);

    if (this.data.networkable && !this.el.hasAttribute("networked")) {
      try {
        this.el.setAttribute("networked", {
          template: "#static-media"
        });
      } catch (_) {
        /* ignore networked id errors */
      }
    }
  },

  _resetRuntimeState() {
    this._ready = false;
    this._modelRoot = null;
    this._mixer = null;
    this._animations = [];
    this._currentActions.forEach(a => {
      try {
        a.stop();
      } catch (_) {
        /* ignore stop errors */
      }
    });
    this._currentActions.length = 0;
    this._originalColors = { material: {}, emissive: {} };
  },

  _onModelLoaded() {
    // Resolve refs
    this._modelRoot = this.el.getObject3D("mesh") || this.el.object3D;
    if (!this._modelRoot) {
      // Ensure something is set so parent wrappers with only children don't appear empty
      this.el.setObject3D("mesh", new THREE.Group());
      this._modelRoot = this.el.getObject3D("mesh");
    }

    // If wrapper name was set prior to load, ensure object3D carries it so getObjectByName finds it
    try {
      if (this.el && this.el.object3D && typeof this.el.object3D.name === "string") {
        const dataName = this.el.getAttribute("name");
        if (dataName) this.el.object3D.name = dataName;
      }
    } catch (_) {
      /* ignore name sync */
    }
    const mixerCmp = this.el.components["animation-mixer"];
    if (mixerCmp) {
      this._mixer = mixerCmp.mixer || null;
      if (Array.isArray(mixerCmp.animations)) {
        this._animations = mixerCmp.animations;
      } else if (Array.isArray(this._modelRoot?.animations)) {
        this._animations = this._modelRoot.animations;
      }
    }

    this._ready = true;
    // Flush queued props
    const pendingKeys = Object.keys(this._pending);
    for (let i = 0; i < pendingKeys.length; i++) {
      this._apply(pendingKeys[i], this._pending[pendingKeys[i]]);
    }
    this._pending = {};

    // Notify
    for (let i = 0; i < this._readyResolvers.length; i++) this._readyResolvers[i]();
    this._readyResolvers.length = 0;
    this.el.emit("nr-model-ready", {
      mixer: this._mixer,
      animations: this._animations,
      root: this._modelRoot
    });
  },

  _onModelError(e) {
    // Keep pending so future src changes can succeed
    // Surface error for diagnostics
    // eslint-disable-next-line no-console
    console.error("scriptable-model: model-error", e?.detail || e);
  },

  _onComponentChanged(e) {
    if (this._syncing) return;
    const name = e?.detail?.name;
    if (!name) return;

    // Two-way: reflect external changes back into our data
    const reflect = () => {
      this._syncing = true;
      const d = this.el.getAttribute("scriptable-model") || {};
      if (name === "shadow") {
        const sh = this.el.getAttribute("shadow") || {};
        d.castShadow = !!sh.cast;
        d.receiveShadow = !!sh.receive;
      } else if (name === "billboard") {
        d.billboard = this.el.hasAttribute("billboard");
      } else if (name === "collidable" || name === "walkable") {
        d[name] = this.el.hasAttribute(name);
      }
      this.el.setAttribute("scriptable-model", d);
      this._syncing = false;
    };

    if (name === "shadow" || name === "billboard" || name === "collidable" || name === "walkable") {
      reflect();
    }
  },

  _applyOrQueue(key, value) {
    if (!this._ready) {
      this._pending[key] = value;
      return;
    }
    this._apply(key, value);
  },

  _apply(key, value) {
    if (this._lastApplied[key] === value) return;
    this._lastApplied[key] = value;

    switch (key) {
      case "castShadow":
      case "receiveShadow": {
        const current = this.el.getAttribute("shadow") || {};
        if (key === "castShadow") current.cast = !!value;
        if (key === "receiveShadow") current.receive = !!value;
        this.el.setAttribute("shadow", current);
        break;
      }
      case "billboard": {
        if (value) this.el.setAttribute("billboard", "");
        else this.el.removeAttribute("billboard");
        break;
      }
      case "collidable":
      case "walkable": {
        if (value) this.el.setAttribute(key, "");
        else this.el.removeAttribute(key);
        break;
      }
      case "enableMaterialColor":
      case "materialColor":
      case "emissiveColor": {
        this._applyMaterialOverrides();
        break;
      }
      case "enableOnProximity":
      case "proximityRadius": {
        // Stored for loop-activation; if you later bind to loop-animation you can read here
        break;
      }
      case "activeClipItems": {
        this._applyAnimations(value);
        break;
      }
      default:
        break;
    }
  },

  _applyMaterialOverrides() {
    if (!this._modelRoot) return;
    const enable = !!this.data.enableMaterialColor;
    const colorHex = new THREE.Color(this.data.materialColor || 0xffffff);
    const emissiveHex = new THREE.Color(this.data.emissiveColor || 0x000000);

    this._modelRoot.traverse(obj => {
      if (!obj || (!obj.isMesh && obj.type !== "Mesh") || !obj.material) return;
      const mat = obj.material;
      const matName = mat.name || obj.uuid;

      if (enable) {
        if (mat.color) {
          if (!this._originalColors.material[matName]) {
            this._originalColors.material[matName] = mat.color.clone();
          }
          mat.color.copy(colorHex);
        }
        if (mat.emissive) {
          if (!this._originalColors.emissive[matName]) {
            this._originalColors.emissive[matName] = mat.emissive.clone();
          }
          mat.emissive.copy(emissiveHex);
        }
      } else {
        if (mat.color && this._originalColors.material[matName]) {
          mat.color.copy(this._originalColors.material[matName]);
        }
        if (mat.emissive && this._originalColors.emissive[matName]) {
          mat.emissive.copy(this._originalColors.emissive[matName]);
        }
      }
      mat.needsUpdate = true;
    });
  },

  _applyAnimations(activeClipItems) {
    if (!this._mixer || !Array.isArray(this._animations)) return;

    // Stop previous
    for (let i = 0; i < this._currentActions.length; i++) {
      try {
        this._currentActions[i].stop();
      } catch (_) {
        /* ignore stop errors */
      }
    }
    this._currentActions.length = 0;

    if (!Array.isArray(activeClipItems) || activeClipItems.length === 0) {
      return;
    }

    // Names preferred; fallback to index
    const names = activeClipItems
      .filter(i => i && (typeof i.label === "string" || typeof i.value === "number"))
      .map(i => (typeof i.label === "string" ? i.label : this._animations[i.value]?.name))
      .filter(Boolean);

    for (let i = 0; i < names.length; i++) {
      const name = names[i];
      const clip = this._animations.find(c => c && c.name === name);
      if (!clip) continue;
      try {
        const action = this._mixer.clipAction(clip, this._modelRoot);
        action.enabled = true;
        action.setLoop(THREE.LoopRepeat, Infinity);
        action.play();
        this._currentActions.push(action);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("scriptable-model: failed to start action", name, e);
      }
    }
  }
});
